import {
  type User,
  setIsCertificateInstancesRequested,
  setIsUsersRequested,
} from '../../../Store/DataSlice'
import './UserAdd.scss'
import { useEffect, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import { isValidEmail } from '../../../Utils/Validations'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { CheckboxOption } from '../../../Components/CheckboxOption/CheckboxOption'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { type RootState } from '../../../Store/Store'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'
import { sortResultString } from '../../../Utils/Arrays'

const emptyCompany: User = {
  email: '',
  isAdmin: false,
  certificateInstanceVatNumber: undefined,
}

export function UserAdd() {
  const dispatch = useDispatch()
  const localization = useLocalization('user')

  const isUsersRequested = useSelector(
    (state: RootState) => state.data.isUsersRequested
  )
  useEffect(() => {
    if (!isUsersRequested) dispatch(setIsUsersRequested(true))
  }, [isUsersRequested, dispatch])
  const allUsers = useSelector((state: RootState) => state.data.users)

  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  const [user, setUser] = useState<User>({ ...emptyCompany })
  useEffect(() => {
    if (
      allCertificateInstances.length > 0 &&
      !user.isAdmin &&
      user.certificateInstanceVatNumber === undefined
    )
      setUser((p) => ({
        ...p,
        certificateInstanceVatNumber: allCertificateInstances[0].vatNumber,
      }))
  }, [allCertificateInstances, user])

  const onEmailChange = (newEmail: string) => {
    if (isValidEmail(newEmail) && allUsers.some((c) => c.email === newEmail)) {
      dispatch(
        setDestinationView({
          destinationView: 'user-detail',
          detailKey: newEmail,
        })
      )
      return
    }
    setUser((p) => ({ ...p, email: newEmail }))
  }

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []
    if (user.email && !isValidEmail(user.email))
      newValidationErrors.push({ property: 'email', type: 'format' })
    if (!user.isAdmin && user.certificateInstanceVatNumber === undefined)
      newValidationErrors.push({
        property: 'certificate-instance',
        type: 'isRequired',
      })
    console.log(
      'newValidationErrors',
      JSON.stringify({ newValidationErrors, user })
    )
    setValidationErrors(newValidationErrors)
  }, [user])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveUser = () => {
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/user-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          user,
        }),
      }
    )
      .then(() => {
        dispatch(setIsUsersRequested(false))
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'user-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch company-add failed: `, err)
      })
  }

  return (
    <div className="user-add">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'user-save-in-progress',
            'De gebruiker word momenteel opgeslagen.'
          )}
        </div>
      ) : (
        <>
          <TextInput
            label={localization.text('email', 'Email')}
            isReadOnly={saveInProgress}
            onChange={onEmailChange}
            value={user.email}
            isValid={!validationErrors.some((ve) => ve.property === 'email')}
            helperText={
              validationErrors.find((ve) => ve.property === 'email')?.type ===
              'format'
                ? localization.text(
                    'email-invalid',
                    'Email formaat is incorrect'
                  )
                : undefined
            }
          />

          <CheckboxOption
            value={user.isAdmin}
            onChange={(newValue) => {
              setUser((previous) => ({
                ...previous,
                isAdmin: newValue,
                certificateInstanceVatNumber: newValue
                  ? undefined
                  : allCertificateInstances?.[0]?.vatNumber,
              }))
            }}
            valueName={localization.text('is-admin', 'Is administrator')}
          />

          {!user.isAdmin ? (
            <SingleSelect
              label={localization.text(
                'certificate-instance',
                'Certificaat instantie'
              )}
              items={allCertificateInstances
                .map((certificateInstance) => ({
                  value: certificateInstance.vatNumber,
                  label: certificateInstance.name,
                }))
                .sort((a, b) => sortResultString(a.label, b.label))}
              isReadOnly={saveInProgress}
              onChange={(newValue) => {
                setUser((previous) => ({
                  ...previous,
                  certificateInstanceVatNumber: newValue,
                }))
              }}
              value={user.certificateInstanceVatNumber}
              isValid={
                !validationErrors.some((ve) => ve.property === 'countryCode')
              }
              helperText={
                validationErrors.find((ve) => ve.property === 'countryCode')
                  ?.type === 'isRequired'
                  ? localization.text(
                      'certificate-instance-missing',
                      'Kies een geldige certificaat instantie'
                    )
                  : undefined
              }
            />
          ) : null}

          <div className="buttons">
            {validationErrors.length === 0 && !saveInProgress ? (
              <Button
                importance={'primary'}
                text={localization.text(
                  'safe-add-button',
                  'Voeg gebruiker toe'
                )}
                onClick={saveUser}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
